import posthog from 'posthog-js';

import events from '~/modules/game-events/game-events';
import { RpcServer } from '~/modules/network/rpc/rpc-server';
import { ServerSubscriptionRegistry } from '~/modules/network/rpc/server-subscription-registry';
import { ChannelName, SubscriptionChannels } from '~/modules/remote-mic/network/client/subscriptions';
import { NetworkMessages } from '~/modules/remote-mic/network/messages';
import { REMOTE_MIC_ROOM_CODE_PATTERN } from '~/modules/remote-mic/network/realtime-protocol';
import { ServerTransport } from '~/modules/remote-mic/network/server/transport/interface';
import { RealtimeServerTransport } from '~/modules/remote-mic/network/server/transport/realtime-server';
import RemoteMicManager from '~/modules/remote-mic/remote-mic-manager';
import generateRoomCode from '~/modules/utils/generate-room-code';
import storage from '~/modules/utils/storage';

import { serverHandlers } from './server-handlers';

export const GAME_CODE_KEY = 'room_id_key';
export const GAME_CODE_LENGTH = 5;

export const storeGameCode = (gameCode: string) => {
  storage.session.setItem(GAME_CODE_KEY, gameCode);
};

// Codes stored before they dropped their fixed `r` lead are one letter short — keep them working.
// Anything else that isn't a valid code is dropped, so the server generates a fresh one
const readStoredGameCode = () => {
  const stored: unknown = storage.session.getItem(GAME_CODE_KEY);
  if (typeof stored !== 'string') return null;
  const code = stored.length === GAME_CODE_LENGTH - 1 ? `r${stored}` : stored;
  return REMOTE_MIC_ROOM_CODE_PATTERN.test(code) ? code : null;
};

export class NetworkServer {
  private gameCode = readStoredGameCode()!;
  private started = false;
  /** Whether the current transport ever came up — tells a lost connection from a failed one. */
  private connected = false;
  private transport: ServerTransport | undefined;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;

  private rpcServer = new RpcServer<typeof serverHandlers, SubscriptionChannels>(
    serverHandlers,
    (peerId) => RemoteMicManager.getPermission(peerId),
    (channel, message) => RemoteMicManager.broadcastToChannel(channel, message),
    (peerId) => this.transport?.removePlayer(peerId),
  );

  // Only the last-value cache half of the registry is used here: which mic is subscribed to what
  // lives in RemoteMicManager, which remembers it across a host reload and does the fan-out.
  private subscriptions = new ServerSubscriptionRegistry<SubscriptionChannels>();

  public constructor() {
    if (!this.gameCode) {
      this.gameCode = generateRoomCode(GAME_CODE_LENGTH);
    }

    global?.addEventListener?.('beforeunload', () => {
      RemoteMicManager.getRemoteMics().forEach((remoteMic) => remoteMic.connection.close());
      this.transport?.disconnect();
    });
  }

  public start = () => {
    this.transport ??= new RealtimeServerTransport();
    if (this.started) return;
    this.started = true;
    console.log('connection started', this.getGameCode());
    storeGameCode(this.gameCode);

    const transport = this.transport;
    transport.connect(
      this.getGameCode(),
      () => {
        console.log('connected', this.getGameCode());
        this.connected = true;
        posthog.capture('remote_mic_server_connected', { transport: transport.name });
        // A reconnect reuses the transport, so the previous connection's listener is still on it
        transport.clearAllListeners();
        transport.addListener((event, sender) => {
          const type = event.t;

          if (type === 'register') {
            RemoteMicManager.addRemoteMic(event.id, event.name, sender, event.silent, event.lag);
          } else if (type === 'unregister') {
            RemoteMicManager.removeRemoteMic(sender.peer);
          } else if (type === 'rpc-sub') {
            RemoteMicManager.addSubscription(sender.peer, event.channel);
            // If there is a cached value for this channel, push it immediately to the new subscriber
            // so they don't have to wait for the next change to receive the current state.
            const lastValue = this.subscriptions.getLastValue(event.channel);
            if (lastValue) {
              sender.send({
                t: 'rpc-pub',
                channel: event.channel,
                data: lastValue.data,
              } as NetworkMessages);
            }
          } else if (type === 'rpc-unsub') {
            RemoteMicManager.removeSubscription(sender.peer, event.channel);
          } else if (type === 'ping') {
            sender.send({ t: 'pong' } as NetworkMessages);
          } else if (type === 'pong') {
            RemoteMicManager.getRemoteMicById(sender.peer)?.onPong();
          } else if (type === 'rpc') {
            this.rpcServer.handleMessage(event, sender);
          }
        });

        events.micServerStarted.dispatch();
      },
      (reason) => {
        // Closed on purpose by `stop()` — it has already reported the server as stopped
        if (this.transport !== transport) return;
        const wasConnected = this.connected;
        events.micServerStopped.dispatch();
        this.started = false;
        posthog.capture(wasConnected ? 'remote_mic_server_connection_lost' : 'remote_mic_server_connection_error', {
          transport: transport.name,
          reason,
        });
        this.connected = false;

        if (reason === 'room-taken') {
          // Another game holds this code in the directory — a fresh code is the only way out
          this.gameCode = generateRoomCode(GAME_CODE_LENGTH);
          storeGameCode(this.gameCode);
        }

        // try to reconnect
        this.reconnectTimer = setTimeout(() => {
          this.reconnectTimer = undefined;
          this.start();
        }, 1_000);
      },
    );
  };

  /** Closes the server, dropping the phones connected to it — `start()` opens a new one. Returns
   * whether there was a server to close (one waiting to reconnect counts), so a caller can restore it. */
  public stop = () => {
    // A connection that dropped on its own has a reconnect queued, which would reopen the server
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    const transport = this.transport;
    if (!transport) return false;
    // Cleared first, so the transport's close callback doesn't schedule a reconnect
    this.transport = undefined;
    this.connected = false;
    RemoteMicManager.getRemoteMics().forEach((remoteMic) => RemoteMicManager.removeRemoteMic(remoteMic.id, true));
    transport.disconnect();
    if (this.started) {
      this.started = false;
      events.micServerStopped.dispatch();
    }
    return true;
  };

  public isStarted = () => this.started;

  public getLatency = () => this.transport?.getCurrentPing() ?? 0;

  // Publish data to all clients subscribed to a named channel
  public publish = <C extends ChannelName>(channel: C, data: SubscriptionChannels[C]): void => {
    this.subscriptions.setLastValue(channel, data);
    this.rpcServer.publish(channel, data);
  };

  // Send a server-initiated call to every currently connected client
  public callAllClients = (method: string, ...args: unknown[]): void => {
    this.rpcServer.broadcastClientCall(RemoteMicManager.getRemoteMics(), method, ...args);
  };

  // Send a server-initiated call to a single connected client
  public callClient = (micId: string, method: string, ...args: unknown[]): void => {
    const mic = RemoteMicManager.getRemoteMicById(micId);
    if (mic) {
      this.rpcServer.callClient(mic.connection, method, ...args);
    }
  };

  public getGameCode = (): string => this.gameCode;
}
